/**
 * Imperative handle the warehouse-account forms expose to their parent. The forms
 * are pure field collectors: they own their RHF state and validation, and let the
 * parent (the add-account drawer header) drive Test/Save by validating and reading
 * the built connection config. Test/Save orchestration lives outside the form.
 */
import type { RetlPrivilegeStatus } from '@server/retl-schema';
import type {
  BigQueryConnectionConfig,
  ClickHouseConnectionConfig,
  DatabricksConnectionConfig,
  PostgresConnectionConfig,
  RedshiftConnectionConfig,
  SnowflakeConnectionInput,
} from '@server/types';
import type { WarehouseProblem } from '@server/warehouse-failure';
import * as React from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/** The connection config produced by any of the warehouse forms. */
export type WarehouseAccountConfig =
  | SnowflakeConnectionInput
  | RedshiftConnectionConfig
  | PostgresConnectionConfig
  | BigQueryConnectionConfig
  | DatabricksConnectionConfig
  | ClickHouseConnectionConfig;

export interface WarehouseFormHandle {
  /** Run validation; resolves true when valid. Invalid fields surface inline errors. */
  validate: () => Promise<boolean>;
  /** Build the typed connection config from the current form values. */
  getValues: () => WarehouseAccountConfig;
  /** The raw field values with the defaults they started from, to carry into a remounted form. */
  getDraft: () => FormDraft;
}

/**
 * What the admin typed, kept across a remount. The add flow remounts the form when the account
 * identity changes, so the new account's prefill runs again (ACT2-1031); the draft is how the
 * credentials typed so far survive it.
 */
export interface FormDraft {
  /** The values the form started from. */
  defaults: Record<string, unknown>;
  /** The values when the draft was taken. */
  values: Record<string, unknown>;
}

const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
const isEmptyValue = (v: unknown): boolean => v === undefined || v === null || v === '';

/**
 * The defaults to mount a form with: `base` (from the prefill), with each value the admin changed in
 * `draft` carried over. A field takes the new prefill only when that prefill moved it to a value, so
 * the identity fields take the new account's dims and a destination's own value still lands; a field
 * whose default did not move, or moved to empty (a typed account prefills nothing), keeps what was
 * typed. `base` is the one taken on first render and is what `getDraft` reports as the defaults.
 */
export function useDraftDefaults<T extends FieldValues>(
  base: T,
  draft: FormDraft | undefined,
): { base: T; defaults: T } {
  const [state] = React.useState(() => {
    if (!draft) return { base, defaults: base };
    const defaults: Record<string, unknown> = { ...base };
    for (const key of Object.keys(base)) {
      if (!(key in draft.values)) continue;
      const edited = !sameValue(draft.values[key], draft.defaults[key]);
      const prefillMoved = !sameValue(draft.defaults[key], base[key]) && !isEmptyValue(base[key]);
      if (edited && !prefillMoved) defaults[key] = draft.values[key];
    }
    return { base, defaults: defaults as T };
  });
  return state;
}

/**
 * Notify the parent whenever any field changes, so it can reset a stale test result.
 * Shared by all four forms — a single RHF watch subscription with cleanup.
 */
export function useResetOnChange<T extends FieldValues>(form: UseFormReturn<T>, onChange?: () => void): void {
  React.useEffect(() => {
    if (!onChange) return;
    const sub = form.watch(() => onChange());
    return () => sub.unsubscribe();
  }, [form, onChange]);
}

/**
 * Wire the standard `{ validate, getValues, getDraft }` imperative handle every warehouse form
 * exposes: validate via RHF, getValues by building the typed config from current values, getDraft
 * by pairing the raw values with `baseDefaults` (from `useDraftDefaults`).
 */
export function useWarehouseFormHandle<T extends FieldValues>(
  ref: React.Ref<WarehouseFormHandle>,
  form: UseFormReturn<T>,
  buildConfig: (values: T) => WarehouseAccountConfig,
  baseDefaults: T,
): void {
  React.useImperativeHandle(
    ref,
    () => ({
      validate: () => form.trigger(),
      getValues: () => buildConfig(form.getValues()),
      getDraft: () => ({ defaults: baseDefaults, values: form.getValues() }),
    }),
    [form, buildConfig, baseDefaults],
  );
}

/** Outcome of a warehouse connection test, keyed per destination in the hook. */
export interface TestResult {
  success: boolean;
  database?: string;
  tableCount?: number;
  error?: string;
  /**
   * The driver's own code on a failed test (ACT2-1116), as `testConnection` sends it: a string such as
   * Snowflake's `'002003'`, or a number. Compare with `sameErrorCode`, never `===`.
   */
  errorCode?: string | number;
  /**
   * Whether the account can write Reverse ETL's internal schema, which every activation needs
   * (ACT2-737). Independent of `success`: a connection with no such access still connects and
   * still saves, it just cannot activate, so this renders as a warning beside a passing test.
   * `status` is the backend's `RetlPrivilegeStatus`; only `missing` is ever shown.
   */
  retlSchema?: { schema: string | null; status: RetlPrivilegeStatus };
  /** A failed test, described for a person (ACT2-1077). `error` is the same words as one sentence. */
  problem?: WarehouseProblem;
}
